import axios from "axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import Tile from "../../Components/Tiles/Tile";
import TileContent from "../../Components/Tiles/TileContent";
import TileContentActions from "../../Components/Tiles/TileContentActions";
import TileContentAction from "../../Components/Tiles/TileContentAction";
import DashboardKpi, { kpiPlaceholder } from "../../Components/Dashboard/DashboardKpi";
import formatNumber from "../../system/formatNumber";
import useAsyncEffect from "../../system/useAsyncEffect";
import useUser from "../../system/useUser";
import { UserRole, WorkSession } from "../../system/Domain";
import { setWorkSession } from "../../system/reducers/workSessions/actions";
import compoundIdsQuery from "./compoundIdsQuery";

interface CompoundMissingPhotosTileProps {
	compoundIds: string[];
}

interface MissingPhotosKpi {
	missingPhotos: number;
}

export default ({ compoundIds }: CompoundMissingPhotosTileProps) => {
	const dispatch = useDispatch();
	const [, , hasRole] = useUser();
	const canStartSessions = hasRole(UserRole.AldUser);
	const [data, setData] = useState<MissingPhotosKpi>();
	// react's dependency comparison is by reference; compoundIds is a new array on every parent
	// render, so depend on a stable joined string instead to avoid an infinite fetch loop.
	const compoundIdsKey = compoundIds.join(",");

	useAsyncEffect(async () => {
		if (compoundIds.length === 0) {
			setData({ missingPhotos: 0 });
			return;
		}

		const { data } = await axios.get<MissingPhotosKpi>(
			`/api/compounds/kpis/missing-photos?${compoundIdsQuery(compoundIds)}`
		);
		setData(data);
	}, [compoundIdsKey]);

	const createWorkSession = async () => {
		const { data: workSession } = await axios.post<WorkSession>(`/api/work-sessions/presales`, {
			perspective: "CompoundMissingPictureServices",
			title: "Fehlende Fotosätze",
			compoundIds
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	return (
		<Tile title="Fehlende Fotosätze">
			<TileContent dense>
				<Box p={2}>
					{data && (
						<DashboardKpi
							primary={formatNumber(data.missingPhotos)}
							secondary={undefined}
						/>
					)}
					{!data && kpiPlaceholder}
				</Box>
				{canStartSessions && (
					<TileContentActions align="right">
						<TileContentAction
							disabled={!data || data.missingPhotos === 0}
							onClick={createWorkSession}
							label="Überprüfen"
						/>
					</TileContentActions>
				)}
			</TileContent>
		</Tile>
	);
};
